import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useCalculateOrderMutation } from '@/app/services/orders'
import { selectLocale } from '@/app/slices/appSlice'
import { selectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import { useCurrencyFormatter } from '@/common/hooks/useCurrencyFormatter'
import { ORDER_STATUS_ARRAY } from '@/common/utils'

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const ItemPrices = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)
    const orderNotCalculated = useSelector(selectedOrderStatus) !== ORDER_STATUS_ARRAY.Calculated

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })

    const { itemPrice, linePrice } = orderItem || {}

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: 1,
            }}
        >
            <ItemRow>
                <Typography
                    data-testid="order-item-prices-section-title"
                    variant="strong1"
                >
                    {t('Prices')}
                </Typography>
            </ItemRow>

            <ItemRow>
                <Typography data-testid="order-item-unit-price-label">{t('Unit price')}</Typography>
                <Typography>
                    {orderNotCalculated || isCalculating || !itemPrice
                        ? String.fromCharCode(8212)
                        : formatCurrency(itemPrice || 0)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="order-item-line-price-label"
                    variant="strong1"
                >
                    {t('Line price')}
                </Typography>
                <Typography variant="strong1">
                    {orderNotCalculated || isCalculating || !linePrice
                        ? String.fromCharCode(8212)
                        : formatCurrency(linePrice || 0)}
                </Typography>
            </ItemRow>
        </Box>
    )
}

ItemPrices.propTypes = {
    orderItemId: PropTypes.string,
}

export default memo(ItemPrices)
